import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GenericTag } from '../../../../ui-components/Tag/GenericTag';

interface MultiChipCellProps {
  /** Distinct values to render as chips (e.g. route tables, next hops). */
  values: string[];
  /** How many chips to show inline before collapsing the rest into a "+N" chip. */
  maxVisible?: number;
  /** Keep the chips on a single line (table cells). When false they wrap (detail panel). */
  singleRow?: boolean;
  /**
   * Stack chips vertically, each filling the column width. Use in a roomy column (the detail
   * panel) so chips consume the available width instead of leaving empty space to their right.
   */
  fillWidth?: boolean;
  /**
   * Truncate long chip labels with a width cap + ellipsis (full value in a tooltip). Default on.
   * Turn off in a roomy column where the full value should show untruncated.
   */
  truncateLabels?: boolean;
}

/**
 * Renders a many-to-one relation (a prefix's route tables / next hops) as a strip of DS
 * `GenericTag` chips. Beyond `maxVisible` it shows a "+N" chip that, on hover, opens a portal
 * popover listing every value — the same interaction as the shared `TagsWithPopover`, but
 * left-aligned for data columns. Long values truncate inside each chip (full value in a tooltip).
 */
export const MultiChipCell: React.FC<MultiChipCellProps> = ({ values, maxVisible = 3, singleRow = false, fillWidth = false, truncateLabels = true }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect>();
  const triggerRef = useRef<HTMLDivElement>(null);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    if (open && triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    }
  }, [open]);

  useEffect(() => () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
    }
  }, []);

  if (values.length === 0) {
    return <span className="text-semantic-text-tertiary">—</span>;
  }

  const visible = values.slice(0, maxVisible);
  const overflow = values.length - visible.length;

  const show = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
    setOpen(true);
  };
  const scheduleHide = () => {
    hideTimeout.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={overflow > 0 ? show : undefined}
        onMouseLeave={overflow > 0 ? scheduleHide : undefined}
        className={
          fillWidth
            ? 'flex flex-col items-stretch gap-1 min-w-0'
            : `flex items-center gap-1 min-w-0 ${singleRow ? 'flex-nowrap' : 'flex-wrap'}`
        }
      >
        {visible.map((value) => (
          <GenericTag
            key={value}
            label={value}
            variant="neutral"
            size="small"
            truncateLabel={truncateLabels}
            tooltip={truncateLabels ? value : undefined}
            /* fillWidth: chip fills the column (stacked). Otherwise cap the width and `!` so it
               truncates even inside a stretched (non-fixed-width) column. When not truncating,
               no width cap so the full value shows. */
            className={fillWidth ? 'w-full' : truncateLabels ? '!max-w-[140px]' : undefined}
          />
        ))}
        {overflow > 0 && <GenericTag label={`+${overflow}`} variant="default" size="small" />}
      </div>

      {open && overflow > 0 && rect &&
        createPortal(
          <div
            onMouseEnter={show}
            onMouseLeave={() => setOpen(false)}
            style={{ position: 'fixed', top: rect.bottom + 6, left: rect.left, zIndex: 9999, maxWidth: 340 }}
            className="rounded-md border border-semantic-border-primary bg-semantic-bg-primary p-3 shadow-lg"
          >
            <div className="flex flex-wrap gap-1.5">
              {values.map((value) => (
                <GenericTag key={value} label={value} variant="neutral" size="small" />
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default MultiChipCell;
